import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useCallback, useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './ProgramsEnrollmentsActivity.module.scss'
import globals from '../../../../../assets/styles/globals.scss'
import { convertHexToRgba } from '../../../../../utility/stylesUtility'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

Tooltip.positioners.cursor = function(elements, coordinates) {
  return { x: coordinates.x, y: coordinates.y }
}

const ProgramsEnrollmentsLineChart = ({ monthlyData }) => {
  const currentYear = new Date().getFullYear()

  const labels = useMemo(() => monthlyData.map(item => {
    const date = new Date(item.month)
    const monthStr = date.toLocaleDateString('en-US', { month: 'short' })
    const yearStr = date.getFullYear() !== currentYear ? ` ${date.getFullYear()}` : ''
    return monthStr + yearStr
  }), [currentYear, monthlyData])

  const values = useMemo(() => {
    const lastNonZeroIndex = monthlyData.reduce((lastIndex, item, index) => (item.value > 0 ? index : lastIndex), -1)

    return monthlyData.map((item, index) => (index > lastNonZeroIndex ? null : item.value))
  }, [monthlyData])

  const programsPerMonth = useMemo(() => monthlyData.map(item => item.courses.length), [monthlyData])

  const externalTooltipHandler = useCallback(context => {
    let tooltipEl = document.getElementById('programs-enrollments-tooltip')

    if (!tooltipEl) {
      tooltipEl = document.createElement('div')
      tooltipEl.id = 'programs-enrollments-tooltip'
      tooltipEl.style.background = '#fff'
      tooltipEl.style.border = `1px solid ${globals.lightGray}`
      tooltipEl.style.padding = '12px 18px'
      tooltipEl.style.borderRadius = '8px'
      tooltipEl.style.boxShadow = '0px 4px 4px rgba(0, 0, 0, 0.2)'
      tooltipEl.style.pointerEvents = 'none'
      tooltipEl.style.position = 'absolute'
      tooltipEl.style.transition = 'all 0.1s ease'
      tooltipEl.style.fontFamily = 'inherit'
      tooltipEl.style.fontSize = '14px'
      tooltipEl.style.zIndex = '9999'
      document.body.appendChild(tooltipEl)
    }

    const tooltipModel = context.tooltip

    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0
      return
    }

    const dataIndex = tooltipModel.dataPoints?.[0]?.dataIndex

    if (dataIndex === undefined) {
      tooltipEl.style.opacity = 0
      return
    }

    const monthData = monthlyData[dataIndex]
    const date = new Date(monthData.month)
    const monthYear = date.getFullYear() < currentYear
      ? date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : date.toLocaleDateString('en-US', { month: 'long' })
    const title = `${monthYear} Enrollments by Programs`
    const total = monthData.value || 0

    let innerHtml = `
      <div style='font-weight:800; font-size: 1rem; color:${globals.darkNavy}; margin-bottom:1rem;'>
        ${title}
      </div>
    `

    const programs = monthData.courses

    innerHtml += `
      <div style='display:flex; justify-content:space-between; font-size: 0.9rem; font-weight:800; color:${globals.darkNavy}; margin-bottom:12px;'>
        <span>Active Programs:</span>
        <span style='flex-shrink:0; margin-left:1rem;'>${programs.length}</span>
      </div>
      <ol style='font-size: 0.9rem; margin:0; padding-left:1.2rem'>
        ${programs.map(program => `
          <li style='margin-bottom:8px; color:${globals.darkNavy}; padding-left:10px;'>
            <div style='
              display:grid;
              grid-template-columns: 1fr auto;
              column-gap: 16px;
              align-items:center;
            '>
              <span>${program.name}</span>
              <span style='font-weight:800; text-align:right;'>${program.value}</span>
            </div>
          </li>
        `)
    .join('')}
      </ol>
    `

    innerHtml += `
      <div style='display:flex; justify-content:space-between; font-weight:800; font-size: 0.9rem; color:${globals.primaryOrange}; margin-top:1rem;'>
        <span>Active Enrollments</span>
        <span style='flex-shrink:0; margin-left:1rem;'>${total}</span>
      </div>
    `

    tooltipEl.innerHTML = innerHtml

    const canvasRect = context.chart.canvas.getBoundingClientRect()
    tooltipEl.style.opacity = 1

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = canvasRect.left + window.pageXOffset + tooltipModel.caretX + 12
    let top = canvasRect.top + window.pageYOffset + tooltipModel.caretY + 12

    const tooltipRect = tooltipEl.getBoundingClientRect()
    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - 8
    }
    if (top + tooltipRect.height > viewportHeight) {
      top = viewportHeight - tooltipRect.height - 8
    }

    tooltipEl.style.left = left + 'px'
    tooltipEl.style.top = top + 'px'
  }, [currentYear, monthlyData])

  const chartData = useMemo(() => ({
    labels,
    datasets: [{
      label: 'Enrollments',
      data: values,
      borderColor: globals.primaryOrange,
      backgroundColor: convertHexToRgba(globals.primaryOrange, 0.25),
      fill: true,
      tension: 0.35,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBackgroundColor: globals.primaryOrange,
      pointHoverBorderColor: globals.primaryOrange,
      borderWidth: 3,
      spanGaps: false,
    }],
  }), [labels, values])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        position: 'cursor',
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: globals.slateGray,
          maxRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          maxTicksLimit: 5,
          color: globals.primaryOrange,
        },
        grid: {
          color: globals.slateGrayIcons,
          borderDash: [2, 4],
          drawTicks: false,
        },
        border: {
          display: false,
        },
      },
    },
  }), [externalTooltipHandler])

  return (
    <div className={classnames('h-100 w-100 d-flex p-5', businessDashboardClasses.sectionWrapper)}>
      <div className='h-100 d-flex flex-column flex-grow-1'>
        <div className={classnames('d-flex flex-column', classes.chartWrapper)}>
          <span className={classnames('mb-3', classes.enrollmentsLabel)}>Enrollments</span>
          <div className='flex-grow-1'>
            <Line data={chartData} options={options} />
          </div>
        </div>
        <div className={classnames('d-flex justify-content-between align-items-center pt-3 mt-2', classes.topBorder)}>
          {programsPerMonth.map((count, index) => (
            <span key={monthlyData[index].month} className={classes.monthlyProgramsCount}>
              {count}
            </span>
          ))}
        </div>
      </div>
      <div className='flex-grow-1 d-flex align-items-end'>
        <span className={classnames('pl-3', classes.programsLabel)}>Programs</span>
      </div>
    </div>
  )
}

ProgramsEnrollmentsLineChart.propTypes = {
  monthlyData: PropTypes.arrayOf(PropTypes.shape({
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    value: PropTypes.number,
    programs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      value: PropTypes.number,
    })),
    courses: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      value: PropTypes.number,
    })),
  })).isRequired,
}

export default ProgramsEnrollmentsLineChart
